import { useCallback, type RefObject } from 'react'
import type { AgGridReact } from 'ag-grid-react'
import type { DagColumnMeta } from '@/types/dag'
import { isCellEditable } from './DagCellRenderer'
import { HIDDEN_ATTRIBUTE_IDS } from './columnConfiguration'
import { useDagTableStore } from '@/stores/useDagTableStore'
import type { RowData } from './useProductsTableState'

const NON_DAG_COLUMNS = new Set(['actions'])

function focusOutsideGrid(container: HTMLElement, backwards: boolean): boolean {
  const candidates = Array.from(
    document.querySelectorAll<HTMLElement>(
      'a[href], button, input, select, textarea, [tabindex]'
    )
  ).filter(
    element =>
      element.tabIndex >= 0 &&
      !container.contains(element) &&
      !element.matches(':disabled') &&
      !element.closest('[inert]') &&
      element.getClientRects().length > 0 &&
      getComputedStyle(element).visibility !== 'hidden'
  )
  const target = (backwards ? candidates.reverse() : candidates).find(element =>
    Boolean(
      container.compareDocumentPosition(element) &
      (backwards
        ? Node.DOCUMENT_POSITION_PRECEDING
        : Node.DOCUMENT_POSITION_FOLLOWING)
    )
  )
  if (!target) return false
  useDagTableStore.getState().clearFocus()
  target.focus()
  return true
}

export function useDagTabNavigation(
  gridRef: RefObject<AgGridReact<RowData> | null>,
  columnsMap: Map<string, DagColumnMeta>,
  isReadOnly = false,
  gridContainerRef?: RefObject<HTMLDivElement | null>
) {
  const setFocusedCell = useDagTableStore(s => s.setFocusedCell)
  const setEditingCell = useDagTableStore(s => s.setEditingCell)

  const resolveTargetAndSetCell = useCallback(
    (rowId: string, targetColId: string, cells: Record<string, unknown>) => {
      const meta = columnsMap.get(targetColId)
      const api = gridRef.current?.api
      const rowIndex = api?.getRowNode(rowId)?.rowIndex
      if (rowIndex != null) api?.ensureIndexVisible(rowIndex)
      api?.ensureColumnVisible(targetColId)

      // Match the special renderers' permissions as well as DAG metadata.
      if (
        !isReadOnly &&
        (targetColId === 'plan_sku_num' ||
          (targetColId === 'description'
            ? !cells.plan_sku_num || !!cells.is_new_sku
            : meta && isCellEditable(meta, cells)))
      ) {
        setEditingCell(rowId, targetColId)
      } else {
        setFocusedCell(rowId, targetColId)
      }
    },
    [gridRef, columnsMap, isReadOnly, setFocusedCell, setEditingCell]
  )

  // Moves focus/editing to the same column on the next/previous displayed
  // row, skipping option rows (which have no DAG cells to land on).
  const navigateEnter = useCallback(
    (
      rowId: string,
      colId: string,
      direction: 'down' | 'up',
      boundaryColId = colId
    ) => {
      const api = gridRef.current?.api
      if (!api) return false

      const currentNode = api.getRowNode(rowId)
      if (currentNode == null || currentNode.rowIndex == null) return false

      const lastIdx = api.getDisplayedRowCount() - 1
      let idx = currentNode.rowIndex

      while (true) {
        idx = direction === 'down' ? idx + 1 : idx - 1
        if (idx < 0 || idx > lastIdx) {
          setFocusedCell(rowId, boundaryColId)
          return false
        }

        const candidate = api.getDisplayedRowAtIndex(idx)
        const candidateData = candidate?.data as RowData | undefined
        if (!candidateData) continue
        if (candidateData.isOptionRow) continue // skip option rows

        resolveTargetAndSetCell(
          candidateData.id,
          colId,
          candidateData.cells ?? {}
        )
        return true
      }
    },
    [gridRef, resolveTargetAndSetCell, setFocusedCell]
  )

  const navigateTab = useCallback(
    (
      rowId: string,
      currentColId: string,
      direction: 'forward' | 'backward'
    ) => {
      const api = gridRef.current?.api
      if (!api) return false

      // Get ordered visible columns, filter to DAG-only
      const allCols = api.getAllDisplayedColumns()
      const dagColIds = allCols
        .map(c => c.getColId())
        .filter(id => !NON_DAG_COLUMNS.has(id) && !HIDDEN_ATTRIBUTE_IDS.has(id))

      const currentIdx = dagColIds.indexOf(currentColId)
      if (currentIdx === -1) return false

      const nextIdx = direction === 'forward' ? currentIdx + 1 : currentIdx - 1

      // At the column boundary — wrap to the neighboring row.
      if (nextIdx < 0 || nextIdx >= dagColIds.length) {
        const wrapColId =
          direction === 'forward'
            ? dagColIds[0]
            : dagColIds[dagColIds.length - 1]
        const moved = navigateEnter(
          rowId,
          wrapColId,
          direction === 'forward' ? 'down' : 'up',
          currentColId
        )
        if (!moved && gridContainerRef?.current) {
          return focusOutsideGrid(
            gridContainerRef.current,
            direction === 'backward'
          )
        }
        return moved
      }

      const targetColId = dagColIds[nextIdx]

      // Get row data for editability check
      const rowNode = api.getRowNode(rowId)
      const cells = (rowNode?.data as RowData | undefined)?.cells ?? {}
      resolveTargetAndSetCell(rowId, targetColId, cells)
      return true
    },
    [gridRef, gridContainerRef, navigateEnter, resolveTargetAndSetCell]
  )

  return { navigateTab, navigateEnter }
}
